import { Accordion } from '@abyss/web/ui/Accordion';
import { Flex } from '@abyss/web/ui/Flex';
import { Markdown } from '@abyss/web/ui/Markdown';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import find from 'lodash/find';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  FilterOptionsType,
  ReverseCoverageTypesCodes,
} from '../../../../common/Constants';
import { getFeatureFlag } from '../../../../common/Utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  Option,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../../../models/ProviderSearch';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import {
  getLagoonConfigValue,
  parseLagoonConfigArray,
} from '../../../../utils/providerSearch.utils';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import { CheckBoxGroup } from '../../../CheckBoxGroup';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { isPrioritySchedulingActive } from '../../../ContentFilters/BccFilterUtils';
import { RadioGroup } from '../../../RadioGroup';
import { FilterDropDown } from '../../FilterDropdown';
import { FilterSlider } from '../../FilterSlider';
import { AllFilterSkeleton } from './AllFilterSkeleton';
import {
  FilterAccordianTrigger,
  FilterAccordionContent,
  FilterAccordionItem,
} from './ProviderFilters.style';

type Props = {
  providerFilters: ProviderFiltersType[];
  selectedFilters: ProviderSearchFilters;
  onFilterSelectionChange: (
    data: ProviderSearchFilters,
    lastModifiedModel: string
  ) => void;
  value?: string[];
  sectionType: string | undefined;
  AccordionType?: string;
  isData: boolean;
};

type OptionTypeProps = {
  isDisabled?: boolean;
  options: Option[];
  model: string;
  label: string;
  type: string;
};

export const ProviderFilters = ({
  providerFilters,
  selectedFilters,
  onFilterSelectionChange,
  value,
  sectionType,
  AccordionType = 'single',
  isData,
}: Props) => {
  const { t } = useTranslation();
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );

  const [
    enableEapFilterUsingBHProgramFF,
    enableVirtualCareFilterAnalytics,
    enhanceDistanceFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_ANALYTICS_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
  ]);

  if (enableEapFilterUsingBHProgramFF) {
    providerFilters = providerFilters.map((filter) => {
      if (filter.model === FilterCollectionModel.AREA_OF_EXPERTISE) {
        const updatedOptions = filter.options.filter(
          (option) => option.dnt_label !== EAP_OPTION_LABEL
        );
        return { ...filter, options: updatedOptions };
      }
      return filter;
    });
  }

  const data = useLagoon('ui-messaging')();
  const UpcomingAvailabilityFilterContent = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS
      .UPCOMING_AVAILABILITY_FILTER_DESCRIPTION,
  });

  const allConfigs = useLagoon('config');
  const pediatricSpecialtyCodesRaw = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.PEDIATRIC_SPECIALTY_CODES
  );
  const pediatricSpecialtyCodes = parseLagoonConfigArray(
    pediatricSpecialtyCodesRaw
  );

  const isPediatricToggleActive =
    selectedFilters['LicensedInPediatrics']?.value;

  const [openedAccordion, setOpenedAccordion] = useState<string>('');
  const [hideSKelton, setHideSkeleton] = useState(false);
  const allFiltersDrawer: string = 'all filters drawer';

  const isVirtualCareFiltersSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_CARE_UNET_FILTER_SUPPRESSION
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare =
    isVirtualCareFiltersSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed);

  const isPrioritySchedulingEnabled =
    isPrioritySchedulingActive(selectedFilters);

  const getLocationForAnalytics = (label: string) =>
    `modal:${allFiltersDrawer}:${label} filter`;

  const onOptionChange = (e: { [key: string]: Option[] }) => {
    const currentSelectedFilters = {
      ...selectedFilters,
      ...e,
    };
    const firstValue = Object.values(e)[0];
    const lastModifiedModel =
      Array.isArray(firstValue) && firstValue.length > 0
        ? Object.keys(e)[0]
        : '';
    onFilterSelectionChange(currentSelectedFilters, lastModifiedModel);
  };

  function onChangeAccordion(e) {
    let linkName;
    let linkType;
    const linkLocation: string = `modal:${allFiltersDrawer}`;

    if (e && openedAccordion !== e) {
      linkName = providerFilters?.find((option) => e === option.model)?.label;
      linkType = 'accordion open';
    } else {
      linkName = providerFilters?.find(
        (option) => openedAccordion === option.model
      )?.label;
      linkType = 'accordion close';
    }

    const selectedModel = providerFilters?.find(
      (option) => e === option.model
    )?.model;

    if (
      selectedModel !== FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
      enableVirtualCareFilterAnalytics
    ) {
      adobeLinkTrackEvent({
        name: linkName,
        location: linkLocation,
        type: linkType,
      });
    }

    setOpenedAccordion(e);
  }
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const isSedationPerformed: any = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SEDATION_DENTISTRY_SERVICES_PERFORMED
  );

  useEffect(() => {
    setHideSkeleton(isData);
  }, []);

  const renderResultBasedOnType = ({
    isDisabled,
    options,
    model,
    label,
    type,
  }: OptionTypeProps) => {
    let filteredOptions: any = options;

    // Filter for Services provided
    if (label == 'Services provided') {
      filteredOptions = isSedationPerformed
        ? options
        : options.filter(
            (option) =>
              option.label !== FilterCollectionModel.SEDATION_DENTISTRY
          );
    }

    // Filter for Specialty when Licensed in Pediatrics is active
    if (
      model === FilterCollectionModel.SPECIALTY_CODE &&
      isPediatricToggleActive
    ) {
      filteredOptions = options.filter((option) =>
        pediatricSpecialtyCodes.includes(option.value)
      );
    }

    if (
      model === FilterCollectionModel.UPCOMING_AVAILABILITY &&
      isPrioritySchedulingEnabled
    ) {
      const hasSevenDayOption = filteredOptions?.some(
        (option) => String(option.value) === '7'
      );

      if (!hasSevenDayOption) {
        filteredOptions = [
          ...filteredOptions,
          {
            label: 'Next 7 days',
            value: '7',
            dnt_label: 'Next 7 days',
          },
        ];
      }
    }

    switch (type) {
      case FilterOptionsType.RADIO:
        return (
          <RadioGroup
            defaultValue={
              selectedFilters[model]?.length
                ? selectedFilters[model][0].value
                : ''
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
          />
        );
      case FilterOptionsType.SLIDER:
        return (
          <FilterSlider
            defaultValue={
              selectedFilters[model]
                ? selectedFilters[model].map((item: Option) => item.value)?.[0]
                : 0
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
          />
        );
      case FilterOptionsType.DROPDOWN:
        return (
          <FilterDropDown
            defaultValue={
              selectedFilters[model]?.length
                ? selectedFilters[model][0].value
                : ''
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
          />
        );
      default:
        return (
          <CheckBoxGroup
            defaultValue={
              Array.isArray(selectedFilters[model])
                ? selectedFilters[model].map((item: Option) => item.value)
                : []
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
          />
        );
    }
  };

  return (
    <Accordion
      data-testid="show-result"
      defaultValue={value}
      isCollapsible
      key={value?.join('-')}
      onValueChange={(e) => onChangeAccordion(e)}
      type={AccordionType}
    >
      {hideSKelton
        ? providerFilters
            ?.filter(({ allFilterDrawerFilters }) =>
              allFilterDrawerFilters[coverageType]?.includes(sectionType)
            )
            .filter((filter) => {
              if (
                filter.model !==
                FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR
              ) {
                if (
                  filter.model !== FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
                  !suppressVirtualCare
                ) {
                  return filter;
                }

                return null;
              }

              return null;
            })
            .sort(
              (a, b) =>
                a.allFilterDrawerOrder[coverageType] -
                b.allFilterDrawerOrder[coverageType]
            )
            .map(({ isDisabled, options, model, label, title, type }) => {
              const isPrioritySchedulingDisabledSection =
                isPrioritySchedulingEnabled &&
                (model === FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
                  model === FilterCollectionModel.UPCOMING_AVAILABILITY ||
                  model === FilterCollectionModel.EAP_OFFERED);

              const effectiveIsDisabled =
                isDisabled || isPrioritySchedulingDisabledSection;

              const shouldShowPrioritySchedulingTooltip =
                effectiveIsDisabled && isPrioritySchedulingDisabledSection;

              const filterTrigger = (
                <FilterAccordianTrigger
                  data-testid={`${label
                    .replace(/ /g, '-')
                    .toLowerCase()}-filter-heading`}
                >
                  <Flex css={{ gap: '8px' }}>{t(title || label)}</Flex>
                </FilterAccordianTrigger>
              );

              const filterContent = (
                <FilterAccordionContent>
                  {label === 'Upcoming availability' && (
                    <span
                      data-testid={`${label
                        .replace(/ /g, '-')
                        .toLowerCase()}-filter-description`}
                    >
                      <Markdown>
                        {UpcomingAvailabilityFilterContent?.message ?? ''}
                      </Markdown>
                    </span>
                  )}
                  {renderResultBasedOnType({
                    isDisabled: effectiveIsDisabled,
                    options,
                    model,
                    label,
                    type,
                  })}
                </FilterAccordionContent>
              );

              return enhanceDistanceFilter &&
                model === FilterCollectionModel.DISTANCE ? (
                <FilterAccordionItem
                  data-testid={`${label
                    .replace(/ /g, '-')
                    .toLowerCase()}-filter`}
                  key={model}
                  value={model}
                >
                  <Flex
                    css={{
                      gap: '8px',
                      color: '$gray7',
                      height: '20px',
                      minHeight: 'unset',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                    }}
                  >
                    {t(title || label)}
                  </Flex>
                  {renderResultBasedOnType({
                    isDisabled,
                    options,
                    model,
                    label,
                    type,
                  })}
                </FilterAccordionItem>
              ) : (
                <FilterAccordionItem
                  data-testid={`${label
                    .replace(/ /g, '-')
                    .toLowerCase()}-filter`}
                  key={model}
                  value={model}
                >
                  {shouldShowPrioritySchedulingTooltip ? (
                    <Tooltip
                      content={String(
                        t(
                          'CONTENT_FILTERS.PRIORITY_SCHEDULING_DISABLED_TOOLTIP'
                        ) || ''
                      )}
                    >
                      <div
                        data-testid={`${label
                          .replace(/ /g, '-')
                          .toLowerCase()}-priority-scheduling-disabled-tooltip`}
                      >
                        {filterTrigger}
                        {filterContent}
                      </div>
                    </Tooltip>
                  ) : (
                    <>
                      {filterTrigger}
                      {filterContent}
                    </>
                  )}
                </FilterAccordionItem>
              );
            })
        : [1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <AllFilterSkeleton index={i} key={i} />
          ))}
    </Accordion>
  );
};
